import { Receipt } from 'lucide-react';
import { formatMoney, formatMonth } from '@blimo/shared';
import { EmptyState } from '../components/empty-state.jsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/table.jsx';

/**
 * Costs per property → ledger account with monthly amount and running cumulative value per month
 * (GET …/finance/costs). Wide table scrolls horizontally; the account column stays visible.
 * @param {{ data: any, showPropertyHeading?: boolean, emptyDescription?: string }} props
 */
export function CostsView({ data, showPropertyHeading = true, emptyDescription }) {
  const properties = data?.properties ?? [];
  if (properties.length === 0) {
    return (
      <EmptyState
        icon={Receipt}
        title="Keine Kosten"
        description={emptyDescription ?? 'Für den gewählten Zeitraum liegen keine Kostenbuchungen auf freigegebenen Konten vor.'}
      />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {properties.length > 1 && (
        <p className="flex flex-wrap items-baseline gap-2 text-sm">
          <span className="text-muted-foreground">Summe aller Liegenschaften</span>
          <span className="numeric text-base font-semibold">{formatMoney(data.total)}</span>
        </p>
      )}
      {properties.map((property) => (
        <section
          key={property.property_id}
          aria-label={`Kosten ${property.name ?? ''}`.trim()}
          data-slot="finance-section"
          className="flex min-w-0 flex-col gap-3 rounded-lg border bg-card p-3 sm:p-4"
        >
          <header className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
            {showPropertyHeading ? <h2 className="text-h3 font-semibold">{property.name}</h2> : <span />}
            <span className="text-sm">
              <span className="text-muted-foreground">Summe </span>
              <span className="numeric font-semibold">{formatMoney(property.total)}</span>
            </span>
          </header>
          {property.accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">Keine Buchungen im gewählten Zeitraum.</p>
          ) : (
            <CostsTable property={property} />
          )}
        </section>
      ))}
    </div>
  );
}

function CostsTable({ property }) {
  const months = property.accounts[0]?.months.map((m) => m.period) ?? [];
  const sticky = 'sticky left-0 z-[1] bg-card';
  return (
    <Table aria-label={`Kosten je Konto ${property.name ?? ''}`.trim()} containerClassName="rounded-md border" className="text-[13px]">
      <TableHeader>
        <TableRow>
          <TableHead rowSpan={2} className={`${sticky} bg-muted align-bottom`}>
            Konto
          </TableHead>
          {months.map((period) => (
            <TableHead key={period} colSpan={2} scope="colgroup" className="border-l text-center">
              {formatMonth(period)}
            </TableHead>
          ))}
          <TableHead rowSpan={2} data-numeric="true" className="border-l align-bottom">
            Summe
          </TableHead>
        </TableRow>
        <TableRow>
          {months.map((period) => [
            <TableHead key={`${period}-a`} data-numeric="true" className="border-l text-[11px] normal-case">
              Monat
            </TableHead>,
            <TableHead key={`${period}-c`} data-numeric="true" className="text-[11px] normal-case">
              kumuliert
            </TableHead>,
          ])}
        </TableRow>
      </TableHeader>
      <TableBody>
        {property.accounts.map((account) => (
          <TableRow key={account.account_number}>
            <TableCell className={`${sticky} min-w-36 max-w-56`}>
              <span className="numeric font-medium">{account.account_number}</span>
              {account.name && <span className="block truncate text-xs text-muted-foreground">{account.name}</span>}
            </TableCell>
            {account.months.map((m) => [
              <TableCell key={`${m.period}-a`} data-numeric="true" className="border-l">
                {formatMoney(m.amount)}
              </TableCell>,
              <TableCell key={`${m.period}-c`} data-numeric="true" className="text-muted-foreground">
                {formatMoney(m.cumulative)}
              </TableCell>,
            ])}
            <TableCell data-numeric="true" className="border-l font-semibold">
              {formatMoney(account.total)}
            </TableCell>
          </TableRow>
        ))}
        <TableRow className="bg-muted/50 font-semibold hover:bg-muted/50">
          <TableCell className={`${sticky} bg-muted`}>Summe</TableCell>
          {property.months?.length
            ? property.months.map((m) => [
                <TableCell key={`${m.period}-a`} data-numeric="true" className="border-l">
                  {formatMoney(m.amount)}
                </TableCell>,
                <TableCell key={`${m.period}-c`} data-numeric="true" className="font-medium text-muted-foreground">
                  {formatMoney(m.cumulative)}
                </TableCell>,
              ])
            : months.length > 0 && <TableCell colSpan={months.length * 2} />}
          <TableCell data-numeric="true" className="border-l">
            {formatMoney(property.total)}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}
